// src/context/CartContext.js
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { cartService } from '../services/api';
import { useAuth } from './AuthContext';

const CartContext = createContext();

export function CartProvider({ children }) {
  const { token } = useAuth();
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(false);

  const total = items.reduce((s, i) => s + i.prix * i.quantite, 0);
  const count = items.reduce((s, i) => s + i.quantite, 0);

  const loadFromLocal = () => {
    try { setItems(JSON.parse(localStorage.getItem('bdk_cart') || '[]')); }
    catch { setItems([]); }
  };

  const loadCart = useCallback(async () => {
    const token = localStorage.getItem('bdk_token');
    if (token) {
      try {
        const data = await cartService.get();
        setItems(data.items || []);
      } catch { loadFromLocal(); }
    } else {
      loadFromLocal();
    }
  }, []);

  // Au login : fusionne le panier invité (localStorage) avec le panier serveur,
  // puis recharge. En invité ou au logout : charge le panier local.
  useEffect(() => {
    async function sync() {
      if (token) {
        try {
          const local = JSON.parse(localStorage.getItem('bdk_cart') || '[]');
          for (const it of local) {
            try { await cartService.add(it.id, it.quantite); } catch {}
          }
          localStorage.removeItem('bdk_cart');
        } catch {}
      }
      loadCart();
    }
    sync();
  }, [token, loadCart]);

  async function addItem(produit, quantite = 1) {
    const token = localStorage.getItem('bdk_token');
    if (token) {
      setLoading(true);
      try { await cartService.add(produit.id, quantite); await loadCart(); }
      finally { setLoading(false); }
    } else {
      setItems(prev => {
        const exist = prev.find(i => i.id === produit.id);
        const updated = exist
          ? prev.map(i => i.id === produit.id ? { ...i, quantite: i.quantite + quantite } : i)
          : [...prev, { ...produit, quantite }];
        localStorage.setItem('bdk_cart', JSON.stringify(updated));
        return updated;
      });
    }
  }

  async function removeItem(id) {
    const token = localStorage.getItem('bdk_token');
    if (token) {
      try { await cartService.remove(id); await loadCart(); } catch {}
    } else {
      setItems(prev => {
        const updated = prev.filter(i => i.id !== id);
        localStorage.setItem('bdk_cart', JSON.stringify(updated));
        return updated;
      });
    }
  }

  async function updateQty(id, quantite) {
    if (quantite <= 0) { removeItem(id); return; }
    const tokenLocal = localStorage.getItem('bdk_token');
    if (tokenLocal) {
      // Connecté : on synchronise avec le serveur (id = id de la ligne panier)
      try { await cartService.update(id, quantite); await loadCart(); } catch {}
    } else {
      setItems(prev => {
        const updated = prev.map(i => i.id === id ? { ...i, quantite } : i);
        localStorage.setItem('bdk_cart', JSON.stringify(updated));
        return updated;
      });
    }
  }

  function clearCart() { setItems([]); localStorage.removeItem('bdk_cart'); }

  return (
    <CartContext.Provider value={{ items, total, count, loading, addItem, removeItem, updateQty, clearCart, loadCart }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() { return useContext(CartContext); }